/**
 * Which weigh-ins the Progress list shows.
 *
 * The server already limits `entries` to the selected range and returns them
 * oldest first. The list shows the newest page (30), and each "Show more"
 * reveals another page of older ones. Changing the range starts over at 30.
 */

/** How many weigh-ins one page of the Progress list holds. */
export const WEIGH_IN_PAGE_SIZE = 30;

export interface WeighInWindow<T> {
  /** Newest first, at most `visible` of them. */
  shown: T[];
  shownCount: number;
  total: number;
  /** True while older weigh-ins in this range are still hidden. */
  hasMore: boolean;
}

/** A whole number of rows. Anything else (negative, NaN, infinite) shows nothing. */
function rowCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}

/**
 * The newest `visible` weigh-ins, newest first.
 *
 * `entries` stays oldest-first and is not modified. Passing `WEIGH_IN_PAGE_SIZE`
 * is the first page; passing it again is how a range change resets the list.
 */
export function visibleWeighIns<T>(entries: readonly T[], visible: number): WeighInWindow<T> {
  const total = entries.length;
  const shownCount = Math.min(total, rowCount(visible));
  // The newest rows sit at the end. Copy that tail, then flip it — never the caller's array.
  const shown = entries.slice(total - shownCount).reverse();
  return { shown, shownCount, total, hasMore: shownCount < total };
}

/** The visible count after one "Show more": another page of older weigh-ins. */
export function expandWeighInWindow(visible: number, pageSize = WEIGH_IN_PAGE_SIZE): number {
  return rowCount(visible) + rowCount(pageSize);
}

/** "Showing 30 of 112". */
export function weighInCountLabel(shown: number, total: number): string {
  return `Showing ${shown} of ${total}`;
}
